import React, { useEffect } from "react";
import { Scale, ShieldCheck, Handshake as HandshakeIcon, Mail } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { COLORS, SERIF, SANS, MONO } from "../components/dispute/theme";
import { useIsMobile, merge } from "../hooks/useIsMobile";

const USE_CASES = [
  { label: "Personal disputes", text: "conflicts between friends, colleagues, or community members" },
  { label: "Business decisions", text: "partners or teams with competing strategies or priorities" },
  { label: "Hiring and evaluation", text: "differences of opinion on candidates or performance" },
  { label: "Community conflicts", text: "moderation disputes, member conduct, contested decisions" },
  { label: "Debates and contested positions", text: "any question where two well-reasoned positions exist" },
];

const VALUES = [
  {
    icon: Scale,
    title: "Both sides matter equally",
    text: "No advantage based on who filed first, who argues more forcefully, or who has more resources.",
  },
  {
    icon: ShieldCheck,
    title: "Neutrality is structural",
    text: "The AI panel has no relationship with either party and no stake in the outcome. Its assessment is based solely on what both sides submit.",
  },
  {
    icon: HandshakeIcon,
    title: "Resolution over winning",
    text: "The goal is not to declare a winner but to help both parties understand where they stand and find a path forward.",
  },
];

const BETA_QUESTIONS = [
  "Does a structured two-sided process produce better outcomes than one-sided AI?",
  "Do both parties feel the process was fair, even when they disagree with the result?",
  "Does having a neutral AI perspective help people reach resolution faster?",
];

export default function About() {
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={s.page}>
      <SiteHeader />

      <main>
        {/* HEADER */}
        <section style={merge(s.header, s.headerMobile, isMobile)}>
          <div style={s.wrap}>
            <span style={s.eyebrow}>About disputefai</span>
            <h1 style={merge(s.h1, s.h1Mobile, isMobile)}>
              Helping people resolve disputes, bridge differences, and make better decisions together.
            </h1>
            <p style={s.lede}>
              disputefai is an early experiment in two-sided AI resolution. We believe most conflicts
              persist not because one side is wrong, but because neither side has ever been heard by
              a genuinely neutral party.
            </p>
          </div>
        </section>

        {/* WHAT WE DO */}
        <section style={s.section}>
          <div style={s.wrap}>
            <span style={s.label}>What we do</span>
            <h2 style={s.h2}>A structured process for any situation where two perspectives need to be heard.</h2>
            <p style={s.p}>
              disputefai isn't just for disputes. It's for any situation where two sides see things
              differently and need a neutral, structured way to work through it — whether that's a
              personal conflict, a business disagreement, or a decision where reasonable people have
              landed in different places.
            </p>

            <div style={s.useCases}>
              {USE_CASES.map((u, i) => (
                <div key={i} style={s.useCase}>
                  <strong style={s.useCaseLabel}>{u.label}</strong>
                  <span>— {u.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY THE PROCESS MATTERS */}
        <section style={s.section}>
          <div style={s.wrap}>
            <span style={s.label}>Why the process matters</span>
            <h2 style={s.h2}>Putting it in writing changes things.</h2>
            <p style={s.p}>
              We've found that the act of articulating your position clearly — committing it to
              writing, sharing it with the other side, and engaging with their perspective — is
              often part of how resolution happens. The process itself creates clarity, even
              before the AI delivers its assessment.
            </p>
            <p style={s.p}>
              <strong style={{ color: COLORS.ink, fontWeight: 600 }}>
                Both sides are required to participate in the same structured way.
              </strong>{" "}
              Neither position is privileged. That symmetry is what makes the result feel fair —
              and what makes it useful.
            </p>

            <div style={s.values}>
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div key={i} style={s.value}>
                    <span style={s.valueIcon}>
                      <Icon size={16} strokeWidth={1.75} />
                    </span>
                    <div>
                      <h4 style={s.valueTitle}>{v.title}</h4>
                      <p style={s.valueText}>{v.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHERE WE ARE NOW */}
        <section style={s.section}>
          <div style={s.wrap}>
            <span style={s.label}>Where we are now</span>
            <h2 style={s.h2}>An early test, honestly described.</h2>
            <p style={s.p}>
              This is not a finished product. It is a limited beta — an experiment designed to
              learn where two-sided AI resolution works and where it doesn't.
            </p>

            <div style={s.betaNote}>
              <span style={s.betaNoteLabel}>We are testing three questions</span>
              <div style={s.betaBullets}>
                {BETA_QUESTIONS.map((q, i) => (
                  <div key={i} style={merge(s.betaBullet, { borderTop: "none" }, i === 0)}>
                    <span style={s.betaDot} />
                    {q}
                  </div>
                ))}
              </div>
            </div>

            <p style={{ ...s.p, marginBottom: 0 }}>
              Every case helps us understand the answers. We're grateful to everyone participating
              in the beta.
            </p>
          </div>
        </section>

        {/* CONTACT */}
        <section style={s.section}>
          <div style={s.wrap}>
            <span style={s.label}>Get in touch</span>
            <h2 style={s.h2}>We'd love to hear from you.</h2>
            <p style={s.p}>
              Questions, feedback, or anything else — reach out. We read everything and respond
              to all of it.
            </p>
            <div style={merge(s.contactCard, s.contactCardMobile, isMobile)}>
              <div>
                <h4 style={s.contactTitle}>Contact the team</h4>
                <p style={s.contactText}>For questions, feedback, or anything else.</p>
              </div>
              <a href="mailto:contact@disputefai.com" style={s.contactEmail}>
                <Mail size={14} strokeWidth={2} />
                contact@disputefai.com
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

const s = {
  // Flex column so SiteFooter's `marginTop: auto` pins it to the bottom of the
  // viewport on short pages — same shell as pageStyles.root.
  page: { background: COLORS.bg, color: COLORS.ink, fontFamily: SANS, minHeight: "100vh", display: "flex", flexDirection: "column" },
  wrap: { maxWidth: 720, margin: "0 auto", padding: "0 32px" },

  eyebrow: { fontFamily: MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#1A5EC4" },
  label: { fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.15em", textTransform: "uppercase", color: "#1A5EC4", marginBottom: 16, display: "block" },

  header: { padding: "72px 0 48px", borderBottom: `1px solid ${COLORS.border}` },
  headerMobile: { padding: "48px 0 36px" },
  h1: { fontFamily: SERIF, fontWeight: 600, fontSize: "clamp(28px, 4.5vw, 42px)", letterSpacing: "-0.3px", lineHeight: 1.15, color: COLORS.ink, margin: "16px 0 18px" },
  h1Mobile: { fontSize: "clamp(26px, 7vw, 32px)" },
  lede: { fontSize: 16.5, color: COLORS.inkSoft, lineHeight: 1.7, maxWidth: "58ch" },

  section: { padding: "44px 0", borderBottom: `1px solid ${COLORS.border}` },
  h2: { fontFamily: SERIF, fontWeight: 600, fontSize: "clamp(20px, 2.8vw, 25px)", letterSpacing: "-0.2px", lineHeight: 1.25, color: COLORS.ink, margin: "0 0 14px" },
  p: { fontSize: 15, color: COLORS.inkSoft, lineHeight: 1.7, margin: "0 0 14px" },

  useCases: { display: "flex", flexDirection: "column", marginTop: 6 },
  useCase: { display: "flex", flexWrap: "wrap", gap: 6, padding: "13px 0", borderTop: `1px solid ${COLORS.border}`, fontSize: 15, color: COLORS.inkSoft, lineHeight: 1.5 },
  useCaseLabel: { color: COLORS.ink, fontWeight: 600 },

  values: { display: "flex", flexDirection: "column", marginTop: 8 },
  value: { display: "flex", gap: 14, alignItems: "flex-start", padding: "16px 0", borderTop: `1px solid ${COLORS.border}` },
  valueIcon: { width: 30, height: 30, borderRadius: "50%", background: COLORS.highlight, color: COLORS.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 },
  valueTitle: { fontFamily: SERIF, fontSize: 16, fontWeight: 600, letterSpacing: "-0.1px", color: COLORS.ink, margin: "0 0 3px" },
  valueText: { fontSize: 13.75, color: COLORS.inkSoft, lineHeight: 1.6, margin: 0 },

  betaNote: { background: COLORS.highlight, border: `1px solid ${COLORS.borderStrong}`, borderRadius: 10, padding: "20px 22px", margin: "16px 0" },
  betaNoteLabel: { fontFamily: MONO, fontSize: 10, letterSpacing: "0.13em", textTransform: "uppercase", color: COLORS.inkSoft, marginBottom: 12, display: "block" },
  betaBullets: { display: "flex", flexDirection: "column" },
  betaBullet: { display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderTop: `1px solid ${COLORS.borderStrong}`, fontSize: 14, color: COLORS.inkSoft, lineHeight: 1.55 },
  betaDot: { width: 5, height: 5, borderRadius: "50%", background: COLORS.ink, flexShrink: 0, marginTop: 7 },

  contactCard: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "22px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" },
  contactCardMobile: { flexDirection: "column", alignItems: "flex-start" },
  contactTitle: { fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: COLORS.ink, margin: "0 0 3px" },
  contactText: { fontSize: 13.5, color: COLORS.inkMuted, lineHeight: 1.5, margin: 0 },
  contactEmail: { display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", background: "#1A5EC4", color: "#fff", borderRadius: 8, fontSize: 13.5, fontWeight: 600, textDecoration: "none", letterSpacing: "-0.1px", whiteSpace: "nowrap", transition: "opacity 0.16s" },
};
